/*
 *   Copyright (c) 2021 vlinder Inc.
 *   All rights reserved.
 *   This software is proprietary of vlinder Inc. and can not be distributed / used for any purpose outside of vlinder Inc. without explicit written consent from the copyright owner.
 */
import { EventBusModule } from '@vlinder-web/event-bus-module-react';
import { BaseApplication } from './base/baseApplication';
import { AppNavigatorService } from '@/AppNavigator';
import { UnitTest } from './unit.test';
import { GraphqlServerModule } from '@vlinder-web/graphql-server-module-react';
import { StorageModule } from '@vlinder-web/storage-module-react';
import { BOOTSTRAP_CONFIG as DEFAULT_APP_CONFIG } from '../config/bootstrap.config';
import { ProductLandingPageService } from '@pages/LandingPage';
import { ProfilePageService } from '@pages/Profile';
import { SignInPageService } from '@pages/SignIn';
import { MyProfilePageService } from '@pages/MyProfile';
import { OrganizationPageService } from '@pages/OrganizationProfile';
import { SuppliersPageService } from '@pages/Suppliers';
import { OrgComplianceListPageService } from '@pages/OrgComplianceList';
import { OrgComplianceDetailPageService } from '@pages/OrgComplianceDetail';
import { UploadDocService } from '@services/uploadService';
import { BulkImportPageService } from '@pages/BulkImport';
import { SupplyChainListPageService } from '@pages/SupplyChainList';
import { SupplyStepperPageService } from '@pages/SupplyStepper';
import { SupplyChainTemplatePageService } from '@pages/SuplyChainTemplate';
import { SuppChainTempOverViewPageService } from '@pages/SupplyChainTemplateOverView';
import { SuppChainTransService } from '@pages/SuppChainTransactions';
import { AnalyticsService } from '@pages/Analytics';
import { SuppChainTransOverViewPageService } from '@pages/SupplyChainTransOverview';
import { TokenizationService } from '@services/tokenization-service';
import { CampaignListPageService } from '@pages/CampaignList';
import { NewCampaignPageService } from '@pages/NewCampaign';
import { BatchSerializationListPageService } from '@pages/BatchSerializationList';
import { NewBatchSerializationPageService } from '@pages/NewBatchSerialization';
import { DistillersPageService } from '@pages/Distillers';
import { DistillersDetailPageService } from '@pages/DistillersDetail';
import { DistillerDetailListPageService } from '@pages/DistillerDetailList';
import { PlantListPageService } from '@pages/PlantList';
import { ProductListPageService } from '@pages/ProductList';
import { SuppChainTransDetailService } from '@pages/SuppChainTransactionDetail';
import { AMFileListPageService } from '@pages/AMFiles';
import { AMFileDetailService } from '@pages/AMFileDetail';
import { PrinterListPageService } from '@pages/Printers';
import { OrganizationDetailPageService } from '@pages/OrganizationDetail';
import { OrgDetailListPageService } from '@pages/OrgDetailList';

export class VApplication extends BaseApplication {
  private static instance: VApplication;

  private constructor(options: any = {}) {
    super(options);

    this.setupBindings();

    //Services
    this.initServices();

    this.initModules();
  }

  static getInstance(options: any = {}) {
    if (!this.instance) {
      this.instance = new VApplication(options);
    }
    return this.instance;
  }

  // @ts-ignore
  async start() {
    await this.initRunLevel();
    await this.unitTest();
  }

  setupBindings() {
    //------------->>>>>--------------//
    //------------->>>>>--------------//
    this.configure('services.UploadDocService', {
      uploadAPI: DEFAULT_APP_CONFIG?.servers?.rest?.uploadServer?.baseURL,
    });
  }

  async initRunLevel() {
    // const storageSrv = StorageModule.getInstance();
    // const DEFAULT_APP_CONFIG = storageSrv.get("appConfig", "session");
    //------------->>>>>--------------//
    const graphqlModule = this.getSync(
      'services.GraphqlServerModule'
    ) as GraphqlServerModule;
    if (graphqlModule) {
      type TClient = (typeof DEFAULT_APP_CONFIG.servers.graphql)[0];
      DEFAULT_APP_CONFIG?.servers.graphql.forEach((client: TClient) => {
        graphqlModule
          .createClient(client.name, {
            url: client.url,
            //@ts-ignore
            getContext: this.getGraphQLContext.bind(this),
          })
          .then((res) => {
            console.log(`initRunLevel check`, res);
          })
          .catch((err) => {
            console.log(`error in initRunLevel  ${err.message}`);
          });
      });
    }
    //------------->>>>>--------------//
    const storageService = this.getSync(
      'services.StorageModule'
    ) as StorageModule;
    await storageService.init();
  }

  initModules() {
    this.service('services.GraphqlServerModule', GraphqlServerModule);
  }

  async unitTest() {
    const ut = new UnitTest(VApplication);
    ut.run();
  }

  initServices() {
    this.service('services.EventBusModule', EventBusModule);
    this.service('services.AppNavigatorService', AppNavigatorService);
    this.service(
      'services.ProductLandingPageService',
      ProductLandingPageService
    );
    this.service('services.ProfilePageService', ProfilePageService);
    this.service('services.SignInPageService', SignInPageService);
    this.service('services.MyProfilePageService', MyProfilePageService);
    this.service('services.OrganizationPageService', OrganizationPageService);
    this.service('services.SuppliersPageService', SuppliersPageService);
    this.service(
      'services.OrgComplianceListPageService',
      OrgComplianceListPageService
    );
    this.service(
      'services.OrgComplianceDetailPageService',
      OrgComplianceDetailPageService
    );
    this.service('services.UploadDocService', UploadDocService);
    this.service('services.BulkImportPageService', BulkImportPageService);
    this.service(
      'services.SupplyChainListPageService',
      SupplyChainListPageService
    );
    this.service('services.SupplyStepperPageService', SupplyStepperPageService);
    this.service(
      `services.SupplyChainTemplatePageService`,
      SupplyChainTemplatePageService
    );
    this.service(
      `services.SuppChainTempOverViewPageService`,
      SuppChainTempOverViewPageService
    );
    this.service(`services.SuppChainTransService`, SuppChainTransService);
    this.service(`services.AnalyticsService`, AnalyticsService);
    this.service(
      `services.SuppChainTransOverViewPageService`,
      SuppChainTransOverViewPageService
    );
    this.service('services.TokenizationService', TokenizationService);
    this.service('services.CampaignListPageService', CampaignListPageService);
    this.service('services.NewCampaignPageService', NewCampaignPageService);
    this.service(
      'services.BatchSerializationListPageService',
      BatchSerializationListPageService
    );
    this.service(
      'services.NewBatchSerializationPageService',
      NewBatchSerializationPageService
    );
    this.service('services.DistillersPageService', DistillersPageService);
    this.service(
      'services.DistillersDetailPageService',
      DistillersDetailPageService
    );
    this.service(
      'services.DistillerDetailListPageService',
      DistillerDetailListPageService
    );
    this.service('services.PlantListPageService', PlantListPageService);
    this.service('services.ProductListPageService', ProductListPageService);
    this.service(
      'services.SuppChainTransDetailService',
      SuppChainTransDetailService
    );
    this.service('services.AMFileListPageService', AMFileListPageService);
    this.service('services.AMFileDetailService', AMFileDetailService);
    this.service('services.PrinterListPageService', PrinterListPageService);
    this.service(
      'services.OrganizationDetailPageService',
      OrganizationDetailPageService
    );
    this.service('services.OrgDetailListPageService', OrgDetailListPageService);
  }

  private getGraphQLContext() {
    const loginService = this.getSync(
      'services.AppNavigatorService'
    ) as AppNavigatorService;
    let token = loginService?.getToken();
    //FIXME:
    //if (token) {
    return {
      headers: {
        //authentication: token,
        headers: {
          // ...{ ...deviceInfoService?.getDeviceInfo() },
          // ...{ ...deviceInfoService?.getAppInfo() },
        },
      },
    };
    // } else {
    //   return {
    //     headers: {
    //       headers: {
    //         // ...{ ...deviceInfoService?.getDeviceInfo() },
    //         // ...{ ...deviceInfoService?.getAppInfo() },
    //       },
    //     },
    //   };
    // }
  }
}
